import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

type SocketPos = { row: number; col: number };
type ParsedSocket = { color: string; position: SocketPos };

@Component({
  selector: 'app-sockets-display',
  templateUrl: './sockets-display.component.html',
  styleUrl: './sockets-display.component.scss',
})
export class SocketsDisplayComponent implements OnInit, OnChanges {
  @Input() sockets?: string;

  parsedSockets: ParsedSocket[] = [];
  socketsLinked = false;
  linkedToNext: boolean[] = [];

  socketsContainerStyle: Record<string, string> = {};
  socketStyles: Record<string, string>[] = [];
  socketLinks: { from: ParsedSocket; to: ParsedSocket }[] = [];
  socketLinkStyles: Record<string, string | undefined>[] = [];

  ngOnInit(): void {
    this.parseSockets();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sockets'] && !changes['sockets'].firstChange) {
      this.parseSockets();
    }
  }

  private parseSockets() {
    this.parsedSockets = [];
    this.socketsLinked = false;
    this.linkedToNext = [];

    if (!this.sockets) {
      this.socketsContainerStyle = {};
      this.socketLinks = [];
      this.socketStyles = [];
      this.socketLinkStyles = [];
      return;
    }

    let socketString = this.sockets;

    // Game clipboard format: "B-R-R-R" or "B-B-G-B G" (dashes = links, spaces = groups)
    if (/[-\s]/.test(socketString) && !socketString.startsWith('L')) {
      this.parseGameFormatSockets(socketString);
      this.finalizeSocketStyles();
      return;
    }

    if (socketString.startsWith('L')) {
      this.socketsLinked = true;
      socketString = socketString.substring(1);
    }

    // Z-shape positions (max 2x3 grid)
    const positions: SocketPos[] = [
      { row: 0, col: 0 },
      { row: 0, col: 1 },
      { row: 1, col: 1 },
      { row: 1, col: 0 },
      { row: 2, col: 0 },
      { row: 2, col: 1 },
    ];

    for (let i = 0; i < Math.min(socketString.length, 6); i++) {
      const color = this.socketCharToColor(socketString[i]);
      if (!color) continue;

      this.parsedSockets.push({
        color,
        position: positions[i],
      });
    }

    this.linkedToNext = this.parsedSockets.slice(0, -1).map(() => this.socketsLinked);
    this.finalizeSocketStyles();
  }

  private parseGameFormatSockets(value: string) {
    const positions: SocketPos[] = [
      { row: 0, col: 0 },
      { row: 0, col: 1 },
      { row: 1, col: 1 },
      { row: 1, col: 0 },
      { row: 2, col: 0 },
      { row: 2, col: 1 },
    ];

    const groups = value.trim().split(/\s+/);
    for (const group of groups) {
      const colors = group.split('-').filter(c => c.length > 0);
      for (let i = 0; i < colors.length; i++) {
        if (this.parsedSockets.length >= 6) return;

        const color = this.socketCharToColor(colors[i]);
        if (!color) continue;

        if (this.parsedSockets.length > 0) {
          this.linkedToNext.push(i > 0);
        }
        this.parsedSockets.push({
          color,
          position: positions[this.parsedSockets.length],
        });
      }
    }

    this.socketsLinked = this.linkedToNext.some(linked => linked);
  }

  private socketCharToColor(socketChar: string): string | undefined {
    switch (socketChar.toUpperCase()) {
      case 'R':
        return '#ca0d32';
      case 'G':
        return '#9ecb0d';
      case 'B':
        return '#5882fe';
      case 'X':
      case 'W':
      case 'S':
        return '#c8c8c8';
      case 'T':
        return 'rgba(0, 0, 0, 0)';
      default:
        return undefined;
    }
  }

  private finalizeSocketStyles() {
    this.socketsContainerStyle = this.getSocketsStyle();
    this.socketLinks = this.getSocketLinks();
    this.socketStyles = this.parsedSockets.map(s => this.getSocketStyle(s));
    this.socketLinkStyles = this.socketLinks.map(l => this.getSocketLinkStyle(l.from, l.to));
  }

  private getSocketsStyle() {
    const usedRows = this.getUsedRows();
    const actualHeight = this.getActualSocketsHeight(usedRows);

    return {
      'display': 'inline-block',
      'position': 'relative',
      'vertical-align': 'middle',
      'margin-left': '6px',
      'width': '8px',
      'height': actualHeight + 'px',
    };
  }

  private getUsedRows(): number[] {
    const rows = new Set<number>();
    this.parsedSockets.forEach(socket => {
      rows.add(socket.position.row);
    });
    return Array.from(rows).sort();
  }

  private getActualSocketsHeight(usedRows: number[]): number {
    if (usedRows.length === 0) return 0;
    if (usedRows.length === 1) return 3;
    if (usedRows.length === 2) return 8;
    return 13;
  }

  private getRemappedRowPosition(originalRow: number, usedRows: number[]): number {
    const rowIndex = usedRows.indexOf(originalRow);
    return rowIndex * 5;
  }

  private getSocketStyle(socket: ParsedSocket) {
    const usedRows = this.getUsedRows();
    const left = socket.position.col * 5;
    const top = this.getRemappedRowPosition(socket.position.row, usedRows);

    return {
      'position': 'absolute',
      'width': '3px',
      'height': '3px',
      'background-color': socket.color,
      'left': left + 'px',
      'top': top + 'px',
    };
  }

  private getSocketLinkStyle(fromSocket: ParsedSocket, toSocket: ParsedSocket) {
    const usedRows = this.getUsedRows();

    const fromLeft = fromSocket.position.col * 5 + 1;
    const fromTop = this.getRemappedRowPosition(fromSocket.position.row, usedRows) + 1;
    const toLeft = toSocket.position.col * 5 + 1;
    const toTop = this.getRemappedRowPosition(toSocket.position.row, usedRows) + 1;

    const isHorizontal = fromSocket.position.row === toSocket.position.row;
    const isVertical = fromSocket.position.col === toSocket.position.col;

    if (isHorizontal) {
      const width = Math.abs(toLeft - fromLeft);
      const left = Math.min(fromLeft, toLeft);
      return {
        'position': 'absolute',
        'width': width + 'px',
        'height': '1px',
        'background-color': 'white',
        'left': left + 'px',
        'top': fromTop + 'px',
      };
    }

    if (isVertical) {
      const height = Math.abs(toTop - fromTop);
      const top = Math.min(fromTop, toTop);
      return {
        'position': 'absolute',
        'width': '1px',
        'height': height + 'px',
        'background-color': 'white',
        'left': fromLeft + 'px',
        'top': top + 'px',
      };
    }

    return { 'display': 'none' };
  }

  private getSocketLinks() {
    if (this.parsedSockets.length < 2) {
      return [];
    }

    const links: { from: ParsedSocket; to: ParsedSocket }[] = [];
    for (let i = 0; i < this.parsedSockets.length - 1; i++) {
      if (!this.linkedToNext[i]) continue;

      const from = this.parsedSockets[i];
      const to = this.parsedSockets[i + 1];

      const fromRow = from.position.row;
      const fromCol = from.position.col;
      const toRow = to.position.row;
      const toCol = to.position.col;

      if (fromRow === toRow || fromCol === toCol) {
        links.push({ from, to });
      } else {
        const intermediateSocket: ParsedSocket = {
          color: 'transparent',
          position: { row: fromRow, col: toCol },
        };
        links.push({ from, to: intermediateSocket });
        links.push({ from: intermediateSocket, to });
      }
    }
    return links;
  }
}
